import React, { useEffect, useState } from 'react';
import Portal from '../../Portal/Portal';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';
import { toFitWidthThumbnailUrl, toZoomedChartUrl, toZoomedPdfUrl } from './pdfViewerUrl';

export interface PdfViewerPage {
  id: string;
  image: string;
  isIncluded: boolean;
}

interface PdfPagesViewerModalProps {
  isVisible: boolean;
  title: string;
  pages: PdfViewerPage[];
  initialPageId: string | null;
  onClose: () => void;
  // Omit when there's nothing to include/exclude (e.g. the single mandatory
  // main screenshot) - hides the checkboxes and Select all/Unselect all.
  onToggleInclude?: (pageId: string, isIncluded: boolean) => void;
  // 'pdf' (default): portrait letter page, fixed zoom. 'chart': wide/landscape
  // screenshot fit to width, since the underlying page is much taller than
  // the chart content actually uses.
  contentKind?: 'pdf' | 'chart';
}

// Full-screen viewer for a single uploaded PDF's pages. Opens on a thumbnail
// grid of every page (same iframe-of-the-signed-URL technique as the small
// cards on the report page, just bigger), and clicking a thumbnail zooms
// into that one page. Every page carries the include/exclude checkbox used
// to build the final report, plus Select all / Unselect all shortcuts.
// Nothing is ever downloaded - it's the same S3 URL rendered at a different
// size.
const PdfPagesViewerModal: React.FC<PdfPagesViewerModalProps> = ({
  isVisible,
  title,
  pages,
  initialPageId,
  onClose,
  onToggleInclude,
  contentKind = 'pdf',
}) => {
  // Tracked locally rather than driven straight off `pages` - the redux
  // state behind that prop isn't updated after updateReportInclude resolves
  // (see store/models/reports.ts), so a checkbox bound directly to it would
  // appear to snap back right after being toggled.
  const [includedById, setIncludedById] = useState<Record<string, boolean>>({});
  const [zoomedPageId, setZoomedPageId] = useState<string | null>(null);
  const selectable = !!onToggleInclude;

  useEffect(() => {
    if (isVisible) {
      setIncludedById(Object.fromEntries(pages.map((page) => [page.id, page.isIncluded])));
      // Nothing to pick from a single-page document - open straight into the
      // zoomed view instead of a grid with one thumbnail in it.
      const defaultPageId = pages.length === 1 ? pages[0].id : null;
      setZoomedPageId(initialPageId && pages.some((page) => page.id === initialPageId) ? initialPageId : defaultPageId);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (zoomedPageId) {
          setZoomedPageId(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible, zoomedPageId, onClose]);

  if (!isVisible) return null;

  const handleToggle = (pageId: string, checked: boolean) => {
    setIncludedById((prev) => ({ ...prev, [pageId]: checked }));
    onToggleInclude?.(pageId, checked);
  };

  const handleSelectAll = (checked: boolean) => {
    setIncludedById(Object.fromEntries(pages.map((page) => [page.id, checked])));
    pages.forEach((page) => onToggleInclude?.(page.id, checked));
  };

  const zoomedIndex = zoomedPageId ? pages.findIndex((page) => page.id === zoomedPageId) : -1;
  const zoomedPage = zoomedIndex >= 0 ? pages[zoomedIndex] : null;

  return (
    <Portal>
      <div className="fixed inset-0 z-9999 flex flex-col bg-black/95">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-strokedark px-4 py-3 sm:px-6">
          <h3 className="truncate text-base font-semibold text-white sm:text-lg">{title}</h3>
          <div className="flex items-center gap-2">
            {selectable && pages.length > 1 && (
              <>
                <InteractiveButton type={ButtonType.PrimaryInverseWhite} text="Select all" onClick={() => handleSelectAll(true)} />
                <InteractiveButton type={ButtonType.PrimaryInverseWhite} text="Unselect all" onClick={() => handleSelectAll(false)} />
              </>
            )}
            <InteractiveButton type={ButtonType.Primary} text="Close" onClick={onClose} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          {zoomedPage ? (
            <div className={`mx-auto flex flex-col items-center gap-4 ${contentKind === 'chart' ? 'max-w-[1400px]' : 'max-w-4xl'}`}>
              {pages.length > 1 && (
                <div className="flex w-full max-w-[800px] items-center justify-between">
                  <InteractiveButton
                    type={ButtonType.PrimaryInverseWhite}
                    text="Back to all pages"
                    onClick={() => setZoomedPageId(null)}
                  />
                  <div className="flex items-center gap-2">
                    <InteractiveButton
                      type={ButtonType.PrimaryInverseWhite}
                      text="‹ Prev"
                      disabled={zoomedIndex <= 0}
                      onClick={() => setZoomedPageId(pages[zoomedIndex - 1].id)}
                    />
                    <InteractiveButton
                      type={ButtonType.PrimaryInverseWhite}
                      text="Next ›"
                      disabled={zoomedIndex >= pages.length - 1}
                      onClick={() => setZoomedPageId(pages[zoomedIndex + 1].id)}
                    />
                  </div>
                </div>
              )}
              {selectable && (
                <label className="flex cursor-pointer select-none items-center gap-2 self-start rounded bg-white px-3 py-1.5 text-sm font-medium text-black shadow">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer"
                    checked={!!includedById[zoomedPage.id]}
                    onChange={(event) => handleToggle(zoomedPage.id, event.target.checked)}
                  />
                  {pages.length > 1 ? `Page ${zoomedIndex + 1} – Include in report` : 'Include in report'}
                </label>
              )}
              <div
                className="w-full overflow-hidden rounded border border-strokedark bg-white shadow-lg"
                style={
                  contentKind === 'chart'
                    ? { maxWidth: 1400, aspectRatio: '11 / 8.5' }
                    : { maxWidth: 800, aspectRatio: '8.5 / 11' }
                }
              >
                <iframe
                  src={contentKind === 'chart' ? toZoomedChartUrl(zoomedPage.image) : toZoomedPdfUrl(zoomedPage.image)}
                  title={`Page ${zoomedIndex + 1}`}
                  className="h-full w-full border-none"
                />
              </div>
            </div>
          ) : (
            <div
              className="mx-auto grid max-w-6xl gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
            >
              {pages.map((page, index) => (
                <div
                  key={page.id}
                  className="relative cursor-pointer overflow-hidden rounded border border-strokedark bg-white shadow-lg"
                  style={{ aspectRatio: '8.5 / 11' }}
                  onClick={() => setZoomedPageId(page.id)}
                >
                  <iframe
                    src={toFitWidthThumbnailUrl(page.image)}
                    title={`Page ${index + 1}`}
                    className="h-full w-full border-none"
                    style={{ pointerEvents: 'none' }}
                  />
                  {selectable && (
                    <label
                      className="absolute left-2 top-2 z-10 flex cursor-pointer select-none items-center gap-1.5 rounded bg-white px-2 py-1 text-xs font-medium text-black shadow"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 cursor-pointer"
                        checked={!!includedById[page.id]}
                        onChange={(event) => handleToggle(page.id, event.target.checked)}
                      />
                      Include
                    </label>
                  )}
                  <div className="absolute bottom-2 left-2 z-10 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
                    Page {index + 1}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default PdfPagesViewerModal;
